import { encodeFunctionData, parseAbi, type Address, type Hex } from "viem";

/**
 * Catalog of business-contract presets the playground attaches to a
 * swapgate quote. Each preset is bound to a single `position`:
 *
 *   - **AFTER** = "swap → contrato". User pays with X, the swap delivers
 *     `bridgeAsset` (USDT for the Aave supply preset), and the contract
 *     consumes it (e.g. `Aave.supply(USDT)`). The user usually ends up
 *     holding the contract's receipt token (aPolUSDT).
 *
 *   - **BEFORE** = "contrato → swap". User pays with `userHoldsAsset`
 *     (e.g. aPolUSDT). The Executor calls the contract first
 *     (`Aave.withdraw(USDT, amount, executor)`), which surfaces
 *     `bridgeAsset` (USDT) inside the executor, then swaps it to
 *     whatever output token the user picked.
 *
 * Adding a preset:
 *
 *   1. Pick a stable `id` and tag the `position`.
 *   2. Reference the canonical chain + token + contract addresses
 *      (contract is upgradeable → bump id + add a NEW entry; never
 *      mutate an existing one).
 *   3. Describe `bridgeAsset` (always required — what the contract call
 *      consumes/produces in the bridge spot) and, for BEFORE, also
 *      `userHoldsAsset` (what the user must own).
 *   4. `buildCallData` receives `executorAddress` for BEFORE presets so
 *      the recipient/operator argument can be the executor (the user's
 *      address is the wrong target — the executor needs to hold the
 *      `bridgeAsset` for the subsequent swap to work).
 */
export interface AmountPlaceholderSpec {
  /** Byte offset (post `0x`) of the amount slot inside the calldata. */
  readonly offset: number;
  /** Slot length in bytes; defaults to 32 (uint256). */
  readonly length?: number;
}

export type PresetPosition = "AFTER" | "BEFORE";

export interface ContractPresetBuildArgs {
  /** The signer (= user's wallet). */
  readonly fromAddress: Address;
  /** Amount to use in calldata (post-swap output for AFTER; user's input for BEFORE). */
  readonly amount: bigint;
  /** The swapgate Executor address on the preset's chain — required for BEFORE recipients. */
  readonly executorAddress: Address;
}

export interface ContractPreset {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly chainId: number;
  readonly position: PresetPosition;
  readonly contractAddress: Address;
  /** Address that LI.FI's swap output must approve. Often equals contractAddress. */
  readonly approvalAddress?: Address;
  readonly gasLimit: string;
  /**
   * Token the contract call CONSUMES (AFTER) or PRODUCES (BEFORE). In
   * both cases this is the `bridgeToken` of the swapgate request.
   */
  readonly bridgeAsset: Address;
  /**
   * BEFORE only — the asset the user must hold to use this preset.
   * Becomes `fromToken` (the Executor pulls it before calling the
   * contract). For an Aave withdraw, this is the a-token (e.g. aPolUSDT).
   */
  readonly userHoldsAsset?: Address;
  readonly buildCallData: (args: ContractPresetBuildArgs) => Hex;
  /**
   * EXACT_IN flow only — the worker writes the swap-derived amount into
   * this byte range of the calldata before forwarding to LI.FI. Omit
   * for calls where the amount lives in `tx.value` or doesn't need
   * substitution.
   */
  readonly amountPlaceholder?: AmountPlaceholderSpec;
}

// ── Polygon ────────────────────────────────────────────────────────────────
const POLYGON = 137;
const AAVE_V3_POOL_POLYGON: Address =
  "0x794a61358D6845594F94dc1DB02A252b5b4814aD";

// Underlyings
const USDT_POLYGON: Address = "0xc2132D05D31c914a87C6611C10748AEb04B58e8F";
const USDC_NATIVE_POLYGON: Address =
  "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359";
const USDCE_POLYGON: Address = "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174";

// Aave V3 supply receipt tokens (aTokens) on Polygon
const APOL_USDT: Address = "0x6ab707Aca953eDAeFBc4fD23bA73294241490620";
const APOL_USDC_NATIVE: Address = "0xA4D94019934D8333Ef880ABFFbF2FDd611C762BD";
const APOL_USDCE: Address = "0x625E7708f30cA75bfd92586e17077590C60eb4cD";

const aavePoolAbi = parseAbi([
  "function supply(address asset, uint256 amount, address onBehalfOf, uint16 referralCode)",
  "function withdraw(address asset, uint256 amount, address to) returns (uint256)",
]);

/**
 * `supply(asset, amount, onBehalfOf, referralCode)` calldata layout:
 *   selector(4) | asset(32) | amount(32) | onBehalfOf(32) | referralCode(32)
 *                             ^ offset 36, length 32
 *
 * `withdraw(asset, amount, to)` calldata layout:
 *   selector(4) | asset(32) | amount(32) | to(32)
 *                             ^ offset 36, length 32 (same)
 */
const AMOUNT_OFFSET_AAVE = 36;

export const CONTRACT_PRESETS: readonly ContractPreset[] = [
  // ── AFTER · Aave V3 supply ───────────────────────────────────────────
  {
    id: "aave-v3-supply-usdt-polygon",
    name: "Aave V3 · supply USDT (Polygon)",
    description:
      "Pagas con X → swap → USDT → Aave.supply(USDT) → recibes aPolUSDT. Mirrors examples/node/swap-and-invest.ts.",
    chainId: POLYGON,
    position: "AFTER",
    contractAddress: AAVE_V3_POOL_POLYGON,
    approvalAddress: AAVE_V3_POOL_POLYGON,
    gasLimit: "350000",
    bridgeAsset: USDT_POLYGON,
    buildCallData: ({ fromAddress, amount }) =>
      encodeFunctionData({
        abi: aavePoolAbi,
        functionName: "supply",
        args: [USDT_POLYGON, amount, fromAddress, 0],
      }),
    amountPlaceholder: { offset: AMOUNT_OFFSET_AAVE, length: 32 },
  },
  {
    id: "aave-v3-supply-usdc-polygon",
    name: "Aave V3 · supply USDC native (Polygon)",
    description:
      "Pagas con X → swap → USDC (native) → Aave.supply(USDC) → recibes aPolUSDCn.",
    chainId: POLYGON,
    position: "AFTER",
    contractAddress: AAVE_V3_POOL_POLYGON,
    approvalAddress: AAVE_V3_POOL_POLYGON,
    gasLimit: "350000",
    bridgeAsset: USDC_NATIVE_POLYGON,
    buildCallData: ({ fromAddress, amount }) =>
      encodeFunctionData({
        abi: aavePoolAbi,
        functionName: "supply",
        args: [USDC_NATIVE_POLYGON, amount, fromAddress, 0],
      }),
    amountPlaceholder: { offset: AMOUNT_OFFSET_AAVE, length: 32 },
  },
  {
    id: "aave-v3-supply-usdce-polygon",
    name: "Aave V3 · supply USDC.e (Polygon)",
    description:
      "Pagas con X → swap → USDC.e → Aave.supply(USDC.e) → recibes aPolUSDC (legacy).",
    chainId: POLYGON,
    position: "AFTER",
    contractAddress: AAVE_V3_POOL_POLYGON,
    approvalAddress: AAVE_V3_POOL_POLYGON,
    gasLimit: "350000",
    bridgeAsset: USDCE_POLYGON,
    buildCallData: ({ fromAddress, amount }) =>
      encodeFunctionData({
        abi: aavePoolAbi,
        functionName: "supply",
        args: [USDCE_POLYGON, amount, fromAddress, 0],
      }),
    amountPlaceholder: { offset: AMOUNT_OFFSET_AAVE, length: 32 },
  },

  // ── BEFORE · Aave V3 withdraw ────────────────────────────────────────
  {
    id: "aave-v3-withdraw-usdt-polygon",
    name: "Aave V3 · withdraw USDT (Polygon)",
    description:
      "Pagas con aPolUSDT → Aave.withdraw(USDT, →executor) → USDT → swap → recibes X. Mirrors examples/node/before-execute.ts.",
    chainId: POLYGON,
    position: "BEFORE",
    contractAddress: AAVE_V3_POOL_POLYGON,
    // Aave's withdraw burns the aTokens the caller holds — Executor must
    // own them post-pull, no separate approval needed for the underlying.
    approvalAddress: AAVE_V3_POOL_POLYGON,
    gasLimit: "350000",
    bridgeAsset: USDT_POLYGON,
    userHoldsAsset: APOL_USDT,
    buildCallData: ({ amount, executorAddress }) =>
      encodeFunctionData({
        abi: aavePoolAbi,
        functionName: "withdraw",
        // `to` MUST be the Executor — that's where the underlying USDT
        // must land so the subsequent swap leg can spend it.
        args: [USDT_POLYGON, amount, executorAddress],
      }),
    amountPlaceholder: { offset: AMOUNT_OFFSET_AAVE, length: 32 },
  },
  {
    id: "aave-v3-withdraw-usdc-polygon",
    name: "Aave V3 · withdraw USDC native (Polygon)",
    description:
      "Pagas con aPolUSDCn → Aave.withdraw(USDC, →executor) → USDC native → swap → recibes X.",
    chainId: POLYGON,
    position: "BEFORE",
    contractAddress: AAVE_V3_POOL_POLYGON,
    approvalAddress: AAVE_V3_POOL_POLYGON,
    gasLimit: "350000",
    bridgeAsset: USDC_NATIVE_POLYGON,
    userHoldsAsset: APOL_USDC_NATIVE,
    buildCallData: ({ amount, executorAddress }) =>
      encodeFunctionData({
        abi: aavePoolAbi,
        functionName: "withdraw",
        args: [USDC_NATIVE_POLYGON, amount, executorAddress],
      }),
    amountPlaceholder: { offset: AMOUNT_OFFSET_AAVE, length: 32 },
  },
  {
    id: "aave-v3-withdraw-usdce-polygon",
    name: "Aave V3 · withdraw USDC.e (Polygon)",
    description:
      "Pagas con aPolUSDC (legacy) → Aave.withdraw(USDC.e, →executor) → USDC.e → swap → recibes X.",
    chainId: POLYGON,
    position: "BEFORE",
    contractAddress: AAVE_V3_POOL_POLYGON,
    approvalAddress: AAVE_V3_POOL_POLYGON,
    gasLimit: "350000",
    bridgeAsset: USDCE_POLYGON,
    userHoldsAsset: APOL_USDCE,
    buildCallData: ({ amount, executorAddress }) =>
      encodeFunctionData({
        abi: aavePoolAbi,
        functionName: "withdraw",
        args: [USDCE_POLYGON, amount, executorAddress],
      }),
    amountPlaceholder: { offset: AMOUNT_OFFSET_AAVE, length: 32 },
  },
];

/**
 * Filter the registry by chain + position. The playground's dropdown
 * uses this so that selecting "AFTER" only surfaces "supply"-shaped
 * presets and "BEFORE" only surfaces "withdraw"-shaped ones.
 */
export function presetsFor(
  chainId: number,
  position: PresetPosition,
): readonly ContractPreset[] {
  return CONTRACT_PRESETS.filter(
    (p) => p.chainId === chainId && p.position === position,
  );
}

export function findPreset(id: string): ContractPreset | undefined {
  return CONTRACT_PRESETS.find((p) => p.id === id);
}

/**
 * Executor address per chain on which the playground knows a deployed
 * Executor.sol lives. Used as `executorAddress` in BEFORE presets so
 * the `to` parameter of `withdraw` (or analogous methods) lands the
 * bridge asset in the right place.
 */
export const EXECUTOR_ADDRESSES: Readonly<Record<number, Address>> = {
  [POLYGON]: "0x151F3138700E2142b00A1482224D82ADa60AB9BC",
};
